import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, ConfigProvider, Popconfirm, Spin, Tabs } from "antd";
import axios from "axios";
import { FormController } from "formifex";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useReportTabCounts from "../../hooks/useReportTabCounts";
import { useReportLock } from "../../hooks/useReportLock";
import { ReportLock } from "../../services/api-server/report_locks";
import {
	getReportTabs,
	getTabDataAttributeValue,
	getTabForFieldPath,
	buildFieldToTabMap,
	applyTabVisibilityClasses,
} from "../../constants/reportTabs";
import {
	REPORT_REVEAL_ITEM,
	REPORT_VALIDATION_FAILED,
	ReportRevealItemPayload,
	SCROLL_AFTER_OPEN_MS,
} from "../../constants/reportEvents";
import LessonLearnedReportFormFooter from "../../pages/lesson-learned/LessonLearnedReportFormFooter";
import AuditReportFormFooter from "../../pages/reporting/audit/AuditReportFormFooter";
import IncidentReportFormFooter from "../../pages/reporting/incident-report/IncidentReportFormFooter";
import MocReportFormFooter from "../../pages/reporting/moc/MocReportFormFooter";
import NonConformanceReportFormFooter from "../../pages/reporting/non-conformance/NonConformanceReportFormFooter";
import OfiReportFormFooter from "../../pages/reporting/ofi/OfiReportFormFooter";
import { getAuditsList } from "../../services/api-server/audits";
import { getDisciplineList } from "../../services/api-server/discipline";
import { getFindingTypesList } from "../../services/api-server/finding_types";
import { getIncidentReportList } from "../../services/api-server/incident_report";
import { getLessonLearnedList } from "../../services/api-server/lesson_learned";
import { getLessonLearnedClassificationList } from "../../services/api-server/lesson_learned_classification";
import { getLessonLearnedTypeList } from "../../services/api-server/lesson_learned_type";
import { getMoCList } from "../../services/api-server/moc";
import { getNonConformanceList } from "../../services/api-server/non_conformance";
import { getOFIList } from "../../services/api-server/ofi";
import { getOriginList } from "../../services/api-server/origin";
import { getProbabilityScoresList } from "../../services/api-server/probabilities";
import { getSectionList } from "../../services/api-server/section";
import {
	deleteFiles,
	postSaveReportFiles,
	syncNestedDocuments,
} from "../../services/api-server/welldocs";
import { consequenceSeverityTable } from "../../stores/consequence-severity-table";
import { useTenantStore } from "../../stores/tenantStore";
import { useUserStore } from "../../stores/userStore";
import {
	ReportContext,
	SaveReportOptions,
	SaveReportResult,
	TReportType,
} from "../../types/reporting.types";
import { AuthHeader, httpAgent, url } from "../../utils/_exports";
import { alertMessage } from "../../utils/alert";
import Emitter from "../../utils/emitter";
import { getSharePointErrorMessage } from "../../utils/sharepointErrors";
import { getCrudaClass } from "../../utils/lookup_list";
import { getReportingPermissions } from "../../utils/reportingPermissions";
import {
	applyPostSaveResult,
	collectFailedAttachments,
	getNestedListConfig,
	getReportApiPath,
	getReportFileField,
	getReportSharepointType,
	getReportUploadFolderName,
	overlayServerFileTruth,
	pickRemovalBaseline,
	ReportFileEntry,
} from "../../utils/reporting/reportFiles";
import {
	findReportItemIndex,
	REPORT_ITEM_LISTS,
	ReportItemTarget,
} from "../../utils/reporting/reportEditAccess";
import { getReportAuthorEmail } from "../../utils/reporting/reportAuthor";
import { getAuditTypeOptions } from "../../utils/reporting/auditTypes";
import { sanitizeReportFormData } from "../../utils/sanitizeReportFormData";
import scrollToFieldIfVisible from "../../utils/scrollToFieldIfVisible";
import { isFormDataDirty } from "../../utils/formDirtyCompare";
import { socket } from "../../utils/socket";
import {
	checkSuperAdminPriviledge,
	includesIgnoreCase,
} from "../../utils/utils";
import AuditLogButton from "../AuditLogButton";
import CustomDrawer from "../CustomDrawer";
import FailedAttachmentsNotice from "./FailedAttachmentsNotice";
import AuditLogModal from "../Modal/AuditLogModal";
import { getReportCrud } from "./reportingCrud";
import ReportStatusUpdateModal from "./ReportStatusUpdateModal";

// Empty lock values are stored as null, the same as no lock.
const emptyToNull = (value?: string | null) => value || null; // NOSONAR: "" must become null too

// The server's error message, else the error's own, else the fallback.
const getErrorMessage = (error: any, fallback: string) =>
	error?.response?.data?.error || error?.message || fallback; // NOSONAR: an empty message falls back

const FOOTER_COMPONENT_MAP = {
	incident_report: IncidentReportFormFooter,
	audit: AuditReportFormFooter,
	moc: MocReportFormFooter,
	ofi: OfiReportFormFooter,
	non_conformance: NonConformanceReportFormFooter,
	lesson_learned: LessonLearnedReportFormFooter,
};

const TITLE_MAP = {
	audit: "Audit",
	incident_report: "Incident Report (IR)",
	moc: "Management of Change (MoC)",
	ofi: "Opportunity for Improvement (OFI)",
	non_conformance: "Non-Conformance Report (NCR)",
	lesson_learned: "Lesson Learned",
} as const;

const OPEN_STATUS_MAP = {
	in_review: "Initial review",
	changes_required: "Changes required",
	in_progress: "In progress",
	final_review: "Final review",
	revision_required: "Revision required",
	ready_to_close: "Approved - Open",
} as const;

const STATUS_COLOR_MAP: Record<string, string> = {
	draft: "#D9D9D9",
	in_review: "#13C2C2",
	in_progress: "#13C2C2",
	final_review: "#13C2C2",
	ready_to_close: "#A855F7",
	changes_required: "#13C2C2",
	revision_required: "#13C2C2",
	closed_approved: "#00FF75",
	closed_rejected: "#FF4D4F",
};

const STATUS_TEXT_MAP: Record<string, string> = {
	draft: "Draft",
	in_review: "Initial review",
	in_progress: "In progress",
	final_review: "Final review",
	ready_to_close: "Approved - Open",
	changes_required: "Changes requested",
	revision_required: "Revision required",
	closed_approved: "Approved and closed",
	closed_rejected: "Rejected and closed",
};

const REPORT_DATA_API = {
	audit: getAuditsList,
	incident_report: getIncidentReportList,
	moc: getMoCList,
	ofi: getOFIList,
	non_conformance: getNonConformanceList,
	lesson_learned: getLessonLearnedList,
};

type ReportingFormV2Props = {
	isOpen: boolean;
	reportType: TReportType | "lesson_learned";
	activeId?: string;
	taskId?: string;
	initialTabKey?: string;
	// An item to open and scroll to once the report has loaded, such as the
	// finding picked on a report page. initialTabKey should be its list's tab.
	focusItem?: ReportItemTarget;
	onClose: () => void;
	showUpdateStatus?: boolean;
};

const ReportingFormDrawerV2 = ({
	isOpen,
	reportType,
	activeId,
	taskId,
	initialTabKey,
	focusItem,
	onClose,
	showUpdateStatus = false,
}: ReportingFormV2Props) => {
	// LOCAL STATES
	// Tracks the saved record's ID. Undefined = create mode, set = edit mode.
	const [internalActiveId, setInternalActiveId] = useState<string | undefined>(
		activeId
	);
	const [reportData, setReportData] = useState<any>(null);
	// Id of a report created in this drawer session, so views can keep their
	// state through the switch from create to edit mode.
	const [createdRecordId, setCreatedRecordId] = useState<string | null>(null);
	const lastSyncedReportDataRef = useRef<any>(null);
	const pendingReportDataRef = useRef<any>(null);
	const [isLoadingData, setIsLoadingData] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [auditLogOpen, setAuditLogOpen] = useState(false);
	const [isEnteringEditMode, setIsEnteringEditMode] = useState(false);

	// TABS
	const tabsConfig = useMemo(() => getReportTabs(reportType), [reportType]);
	const visibleTabs = useMemo(() => {
		if (reportType !== "ofi") return tabsConfig;
		const isDraftOrCreate = !internalActiveId || reportData?.status === "draft";
		if (isDraftOrCreate) {
			return tabsConfig?.filter((tab) => tab.key !== "follow_up_actions");
		}
		return tabsConfig;
	}, [tabsConfig, reportType, internalActiveId, reportData?.status]);
	const hasTabs = !!visibleTabs?.length;
	const firstTabKey = visibleTabs?.[0]?.key ?? "report_details";
	const [activeTab, setActiveTab] = useState<string>(firstTabKey);
	const [showTabErrorCounts, setShowTabErrorCounts] = useState(false);
	const [validationErrorCounts, setValidationErrorCounts] = useState<
		Record<string, number>
	>({});

	// Reset tab state when drawer opens/closes or report type changes
	useEffect(() => {
		if (isOpen && hasTabs) {
			const targetTab =
				initialTabKey && visibleTabs.some((tab) => tab.key === initialTabKey)
					? initialTabKey
					: firstTabKey;
			// eslint-disable-next-line react-hooks/set-state-in-effect
			setActiveTab(targetTab);
			setShowTabErrorCounts(false);
			setValidationErrorCounts({});
		}
	}, [isOpen, reportType, firstTabKey, hasTabs, initialTabKey, visibleTabs]);

	// STORE
	const isMasterTenant = useTenantStore((state) => state.isMasterTenant);
	const tenantList = useTenantStore((state) => state.tenantList);
	const currentTenantKey = useTenantStore((state) => state.tenant?.role_key);
	const currentUser = useUserStore((state) => state.user);
	const userRole = useUserStore((state) => state.userRole);
	const currentTenantData = useTenantStore((state) => state.tenant);
	const isSuperAdmin = checkSuperAdminPriviledge(userRole);
	const isTenantAdmin = includesIgnoreCase(
		currentTenantData?.tenant_admin,
		currentUser?.email
	);
	const canUpdateStatus = isSuperAdmin || isTenantAdmin;

	// FORM
	const form = FormController.useFormController();

	const handleForceUnlocked = useCallback(() => {
		form.resetForm();
		setReportData((prev: any) =>
			prev
				? {
						...prev,
						locked_by_email: null,
						locked_by_name: null,
						locked_at: null,
					}
				: prev
		);
	}, [form]);

	const handleLockChange = useCallback(
		(lock: ReportLock | null) => {
			// If the lock was just released and we have a pending content update
			// that arrived while the report was still locked, apply the content
			// together with the released lock state so the UI updates atomically.
			// However, if the lock being released belonged to the current user
			// (e.g. after they saved as draft and exited edit mode), do NOT apply
			// the pending fetch – their own mutation already keeps the latest data.
			if (!lock?.locked_by_email && pendingReportDataRef.current) {
				const currentUserEmail = currentUser?.email?.toLowerCase?.();
				const currentUserUpn = currentUser?.upn?.toLowerCase?.();
				const currentUserPreferredUsername =
					currentUser?.preferred_username?.toLowerCase?.();
				const previousLockEmail = reportData?.locked_by_email?.toLowerCase?.();

				const isMyLock = previousLockEmail
					? previousLockEmail === currentUserEmail ||
						previousLockEmail === currentUserUpn ||
						previousLockEmail === currentUserPreferredUsername
					: false;

				if (!isMyLock) {
					const releasedData = {
						...pendingReportDataRef.current,
						locked_by_email: emptyToNull(lock?.locked_by_email),
						locked_by_name: emptyToNull(lock?.locked_by_name),
						locked_at: emptyToNull(lock?.locked_at),
					};
					setReportData(releasedData);
					lastSyncedReportDataRef.current = releasedData;
				}
				pendingReportDataRef.current = null;
				return;
			}

			setReportData((prev: any) =>
				prev
					? {
							...prev,
							locked_by_email: emptyToNull(lock?.locked_by_email),
							locked_by_name: emptyToNull(lock?.locked_by_name),
							locked_at: emptyToNull(lock?.locked_at),
						}
					: prev
			);
		},
		[currentUser, reportData]
	);

	// REPORT LOCK
	// Seed the lock from the already-fetched report data so the drawer can
	// auto-resume edit mode instantly; the active API will still verify/update it.
	const reportLockFromData = useMemo(() => {
		if (!reportData?.locked_by_email) return null;
		return {
			locked_by_email: reportData.locked_by_email,
			locked_by_name: reportData.locked_by_name,
			locked_at: reportData.locked_at,
		};
	}, [reportData]);

	const {
		lock: hookLock,
		loading: isLockLoading,
		acquire,
		release,
		forceUnlock,
	} = useReportLock(
		internalActiveId,
		reportType,
		handleForceUnlocked,
		reportLockFromData,
		handleLockChange
	);

	const effectiveLock = useMemo(
		() => hookLock ?? reportLockFromData,
		[hookLock, reportLockFromData]
	);

	const isLockedByMe = useMemo(() => {
		const lockEmail = effectiveLock?.locked_by_email?.toLowerCase();
		return !!lockEmail && lockEmail === currentUser?.email?.toLowerCase();
	}, [effectiveLock, currentUser?.email]);

	const isLocked = useMemo(
		() => !!effectiveLock?.locked_by_email,
		[effectiveLock]
	);

	const isLockedByOther =
		isLocked && !isLockedByMe && !isLockLoading && !!currentUser?.email;
	// Who holds the lock: their name, or their email when the name is empty.
	const lockOwner =
		effectiveLock?.locked_by_name || effectiveLock?.locked_by_email; // NOSONAR: an empty name falls back to the email

	const tabCounts = useReportTabCounts(form, reportType, visibleTabs ?? []);

	const handleValidationError = useCallback(
		(error: unknown) => {
			// Lets the form's sections react to every failed submit, e.g. the
			// audit findings list opens the findings that hold an error.
			Emitter.emit(REPORT_VALIDATION_FAILED, error);
			if (!visibleTabs?.length) return;

			setShowTabErrorCounts(true);

			const validationError = error as
				{ errorFields?: { name?: string[] | string }[] } | undefined;
			if (!validationError?.errorFields?.length) return;

			const fieldToTabMap = form?.schema?.items
				? buildFieldToTabMap(form.schema.items)
				: {};

			const counts: Record<string, number> = {};
			for (const err of validationError.errorFields) {
				const name = Array.isArray(err.name) ? err.name : [err.name];
				const fieldPath = name.join(".");
				const tab = getTabForFieldPath(fieldPath, visibleTabs, fieldToTabMap);
				if (tab) {
					counts[tab] = (counts[tab] || 0) + 1;
				}
			}
			setValidationErrorCounts(counts);

			const firstErrorField = validationError.errorFields[0];
			const fieldName = Array.isArray(firstErrorField?.name)
				? firstErrorField.name
				: [firstErrorField?.name];
			const fieldPath = fieldName.join(".");

			const targetTab = getTabForFieldPath(
				fieldPath,
				visibleTabs,
				fieldToTabMap
			);

			if (targetTab) {
				setActiveTab(targetTab);
			}
		},
		[form.schema, visibleTabs]
	);

	const handleValidationSuccess = useCallback(() => {
		setValidationErrorCounts({});
		setShowTabErrorCounts(false);
	}, []);

	// REACT QUERY
	const queryClient = useQueryClient();

	const crud = useMemo(() => getReportCrud(reportType), [reportType]);

	// MEMO
	const lookupQueries = useMemo(() => {
		const consequences = consequenceSeverityTable.map((c) => ({
			label: c.consequence,
			value: c.consequence,
		}));

		const reportTypeExtras: Partial<
			Record<TReportType | "lesson_learned", () => Record<string, any>>
		> = {
			lesson_learned: () => ({
				sections: getSectionList(),
				disciplines: getDisciplineList(),
				origins: getOriginList(),
				lesson_learned_types: getLessonLearnedTypeList(),
				lesson_learned_classifications: getLessonLearnedClassificationList(),
			}),
			incident_report: () => ({
				consequences,
				probabilities: getProbabilityScoresList(
					form.formData?.operator ?? currentTenantKey ?? "",
					{},
					{ index: 1 }
				),
			}),
			non_conformance: () => ({
				consequences,
				probabilities: getProbabilityScoresList(
					form.formData?.operator ?? currentTenantKey ?? "",
					{},
					{ index: 1 }
				),
			}),
			audit: () => ({
				audit_types: getAuditTypeOptions(!!form.formData?.internal_report),
				// Append non-conformity to the fetched list
				finding_types: getFindingTypesList().then((list: any) => [
					...list,
					{ key: "non-conformity", name: "Non-Conformity" },
				]),
			}),
		};

		return {
			user: currentUser,
			...(isMasterTenant ? { allTenants: tenantList } : {}),
			...reportTypeExtras[reportType]?.(),
		};
	}, [
		reportType,
		currentUser,
		isMasterTenant,
		tenantList,
		form.formData?.operator,
		form.formData?.internal_report,
	]);

	const isExistingDraft = !!internalActiveId && reportData?.status === "draft";
	const isEditing = isLockedByMe && isExistingDraft;
	const isFormReadOnly =
		isSaving ||
		isLoadingData ||
		isLockedByOther ||
		(isExistingDraft && !isEditing) ||
		(!!internalActiveId && !reportData);

	const reportContext: ReportContext = useMemo(() => {
		const currentUserEmail = currentUser?.email?.toLowerCase();
		const responsibleItems =
			reportType === "audit"
				? reportData?.findings
				: reportData?.follow_up_actions;
		const reportAuthorEmail = (
			getReportAuthorEmail(reportData) ?? ""
		).toLowerCase();
		const additionalAuthors = (reportData?.additional_authors ?? []).map(
			(email: string) => email.toLowerCase()
		);

		return {
			status: reportData?.status ?? null,
			openStatus: reportData?.open_status ?? null,
			isMasterTenant,
			isAuthor:
				!reportData ||
				reportAuthorEmail === currentUserEmail ||
				additionalAuthors.includes(currentUserEmail ?? ""),
			isPrimaryAuthor: !reportData || reportAuthorEmail === currentUserEmail,
			isReviewer: reportData?.reviewer?.toLowerCase() === currentUserEmail,
			isResponsible:
				responsibleItems?.some(
					(item: any) => item.responsible?.toLowerCase() === currentUserEmail
				) ?? false,
			isCommenter:
				reportType === "lesson_learned"
					? reportData?.commenter?.toLowerCase() === currentUserEmail
					: false,
			isSuperAdmin,
			isTenantAdmin,
			currentUser: currentUser ?? null,
			activeRecordId: internalActiveId ?? null,
			mode: internalActiveId ? "edit" : "create",
			isNewlyCreated:
				!!internalActiveId && internalActiveId === createdRecordId,
			isInternalReport:
				reportType === "audit" ? reportData?.internal_report : false,
			isLockedByOther,
			isReadOnly: isFormReadOnly,
		};
	}, [
		reportData,
		currentUser,
		isMasterTenant,
		reportType,
		internalActiveId,
		createdRecordId,
		isSuperAdmin,
		isTenantAdmin,
		isLockedByOther,
		isFormReadOnly,
	]);

	const permissions = useMemo(
		() => getReportingPermissions(reportContext),
		[reportContext]
	);

	const failedAttachments = useMemo(
		() => collectFailedAttachments(reportData),
		[reportData]
	);

	const showEditButton =
		isEditing ||
		isEnteringEditMode ||
		(isExistingDraft &&
			permissions.canEditReport &&
			!isLockedByOther &&
			!isLockLoading);

	let editButtonLabel = "Edit";
	if (isEditing) editButtonLabel = "Exit edit mode";
	else if (isEnteringEditMode) editButtonLabel = "Entering edit mode...";

	const initialData = useMemo(() => {
		// Edit mode: return the fetched record, with a special override for ofi in_review
		if (reportData) {
			if (reportType === "ofi" && reportData.open_status === "in_review") {
				return {
					...reportData,
					follow_up_actions: [
						{ action_number: "#Action 1", status: "pending" },
					],
				};
			}
			return reportData;
		}

		// Create mode: build defaults per report type
		const baseData: Record<string, any> = { operator: currentTenantKey };

		switch (reportType) {
			case "audit":
				return {
					...baseData,
					internal_report: false,
					findings: [{ finding_number: "#Finding 1" }],
				};
			case "incident_report":
				return {
					...baseData,
					is_project_related: false,
					investigation_required: false,
					// relevant_authorities_notified and consequences are hidden by default
					// (displayWhen requires incident_type to be set), so don't pre-populate
					// them; otherwise the normalized dirty check sees missing/hidden keys
					// as changes.
					causes: [
						{
							cause_description: "",
							human: [],
							technical: [],
							organisational: [],
						},
					],
					follow_up_actions: [{ action_number: "#Action 1" }],
				};
			case "moc":
				return {
					...baseData,
					is_project_related: false,
					risk_assessment_completed: false,
					follow_up_actions: [{ action_number: "#Action 1" }],
				};
			case "ofi":
				return {
					...baseData,
					is_anonymous: false,
					is_project_related: false,
				};
			case "non_conformance":
				return {
					...baseData,
					is_project_related: false,
					ncr_type: "temporary",
				};
			case "lesson_learned":
				return {
					...baseData,
					internal_report: false,
				};
			default:
				return baseData;
		}
	}, [reportData, reportType, currentTenantKey]);

	// EFFECTS
	// Keep internalActiveId in sync when the parent changes the activeId prop
	// (e.g. opening a different record without unmounting the drawer).
	useEffect(() => {
		setInternalActiveId(activeId);
		setCreatedRecordId(null);
		setIsEnteringEditMode(false);
		pendingReportDataRef.current = null;
		// The previous record's files must never serve as the removal baseline
		// for the next one; the fetch below sets it again.
		lastSyncedReportDataRef.current = null;
	}, [activeId]);

	// Fetch report data when opening in edit mode.
	// Only re-runs when internalActiveId changes (open/close), not on saves.
	useEffect(() => {
		if (!internalActiveId) {
			setReportData(null);
			return;
		}

		// reportData was already set from the create/update response — no need to re-fetch.
		if (reportData?._id === internalActiveId) return;

		const fetchFn = REPORT_DATA_API[reportType];
		if (!fetchFn) return;

		const fetchData = async () => {
			setIsLoadingData(true);
			try {
				const result = await fetchFn({ _id: internalActiveId });
				if (Array.isArray(result)) {
					setReportData(result[0] ?? null);
					lastSyncedReportDataRef.current = result[0] ?? null;
				}
			} catch (error) {
				console.error("Failed to fetch report data", error);
			} finally {
				setIsLoadingData(false);
			}
		};

		void fetchData();
	}, [internalActiveId]);

	// Re-fetch the form schema whenever the reportType changes OR whenever any
	// reportContext value that controls readOnly/disabled flags changes (mode,
	// status, openStatus, role flags).  Lookup data is fetched separately so
	// it doesn't re-run on every save.
	useEffect(() => {
		if (!isOpen || !reportType) return;
		const fetchSchema = async () => {
			try {
				if (!reportType) throw new Error("Report type is null/undefined");
				const lookupKey = reportType === "audit" ? "audits" : reportType;
				const crudaClassInstance = await getCrudaClass(lookupKey);
				const schema = await crudaClassInstance.getFormList(
					reportContext,
					updateMutation
				);
				if (schema.items) {
					schema.items = applyTabVisibilityClasses(schema.items);
				}
				form.setSchema(schema);
			} catch (error) {
				console.error("Failed to fetch form schema", error);
			}
		};

		void fetchSchema();
	}, [
		isOpen,
		reportType,
		reportContext.mode,
		reportContext.isNewlyCreated,
		reportContext.status,
		reportContext.openStatus,
		reportContext.isAuthor,
		reportContext.isReviewer,
		reportContext.isResponsible,
		reportContext.isCommenter,
		reportContext.isMasterTenant,
		reportContext.isLockedByOther,
		reportContext.isReadOnly,
	]);

	// Fetch lookup data once per report type — these don't change on save.
	useEffect(() => {
		if (!isOpen || !reportType) return;
		const fetchLookupData = async () => {
			const results = await Promise.all(Object.values(lookupQueries));
			const lookupData = Object.fromEntries(
				Object.entries(lookupQueries).map(([key], index) => [
					key,
					results[index],
				])
			);
			form.setLookupData((prev: any) => ({ ...prev, ...lookupData }));
		};

		void fetchLookupData();
	}, [isOpen, reportType, lookupQueries]);

	// Set initial form data whenever reportData changes (edit mode load, post-create, post-update).
	useEffect(() => {
		form.setInitialFormData(initialData);
	}, [initialData]);

	// Opens focusItem once per opening: its list opens it, then it is scrolled
	// into view once the open animation is done. The form first shows the
	// create defaults while the report loads, so this waits until the form
	// holds the loaded item, with its number, at that index.
	const focusHandledRef = useRef(false);
	const focusScrollTimerRef = useRef<ReturnType<typeof setTimeout>>();

	useEffect(() => {
		focusHandledRef.current = false;
		return () => clearTimeout(focusScrollTimerRef.current);
	}, [isOpen, focusItem]);

	useEffect(() => {
		const list = REPORT_ITEM_LISTS[reportType];
		if (!isOpen || !focusItem || !list || focusHandledRef.current) return;
		const loadedId = reportData?._id;
		if (!loadedId || loadedId !== internalActiveId) return;

		const loadedItems = initialData?.[list.name];
		const index = findReportItemIndex(loadedItems, focusItem, list.numberField);
		if (index < 0) {
			// Gone since the page loaded; the drawer still shows the list's tab.
			focusHandledRef.current = true;
			return;
		}
		// The create defaults hold one item, so they can only pass this at index
		// 0. Form.List keeps keys by position, so revealing that key early still
		// opens the loaded item once the form holds it.
		const formItem = form.formData?.[list.name]?.[index];
		const loadedNumber = loadedItems[index]?.[list.numberField];
		if (!formItem || formItem[list.numberField] !== loadedNumber) return;

		focusHandledRef.current = true;
		const reveal: ReportRevealItemPayload = { listName: list.name, index };
		Emitter.emit(REPORT_REVEAL_ITEM, reveal);
		focusScrollTimerRef.current = setTimeout(() => {
			scrollToFieldIfVisible(
				form.formRef,
				[list.name, index, list.scrollField],
				{ behavior: "smooth", block: "center" }
			);
		}, SCROLL_AFTER_OPEN_MS);
	}, [
		isOpen,
		focusItem,
		reportType,
		reportData?._id,
		internalActiveId,
		initialData,
		form.formData,
		form.formRef,
	]);

	// Listen for real-time report updates from other users.
	useEffect(() => {
		if (!internalActiveId || !reportType) return;

		const eventName = `report_updated:${internalActiveId}`;
		const currentUserEmail = currentUser?.email?.toLowerCase();
		const currentUserUpn = currentUser?.upn?.toLowerCase?.();
		const currentUserPreferredUsername =
			currentUser?.preferred_username?.toLowerCase?.();

		const isCurrentUser = (email?: string | null) => {
			if (!email) return false;
			return (
				email === currentUserEmail ||
				email === currentUserUpn ||
				email === currentUserPreferredUsername
			);
		};

		const onReportUpdated = (payload: any) => {
			const updatedBy = payload?.updated_by_email?.toLowerCase?.();
			// Ignore updates triggered by the current user to avoid flicker/overwrite.
			if (isCurrentUser(updatedBy)) return;

			const fetchFn = REPORT_DATA_API[reportType];
			if (!fetchFn) return;

			void fetchFn({ _id: internalActiveId }).then((result: any) => {
				if (!Array.isArray(result)) return;
				const data = result[0];
				if (!data) return;

				if (!data.locked_by_email) {
					// Report is already unlocked: apply content + lock state together.
					setReportData(data);
					lastSyncedReportDataRef.current = data;
					pendingReportDataRef.current = null;
				} else {
					// Report is still being edited: defer the content update until
					// the lock is released so the UI updates atomically.
					pendingReportDataRef.current = data;
				}
			});
		};

		socket.on(eventName, onReportUpdated);

		return () => {
			socket.off(eventName, onReportUpdated);
		};
	}, [internalActiveId, reportType, currentUser?.email]);

	// Override formifex's built-in dirty check with a normalized comparison so that
	// clearing a text field (which produces "") is treated the same as never typing
	// in it (initial value undefined). This keeps the Close/Cancel button accurate.
	useEffect(() => {
		if (!form.formData || !form.initialFormData) return;
		const dirty = isFormDataDirty(form.formData, form.initialFormData);
		if (dirty !== form.isDirty) {
			form.setIsDirty(dirty);
		}
	}, [form.formData, form.initialFormData, form.isDirty]);

	// Coordinate Management Review-specific field behavior:
	// - set Auditee to the master tenant key when audit_type is Management Review
	// - clear Auditee when leaving Management Review
	// - reset audit_type/Auditee if internal_report is turned off while Management Review is selected
	const prevAuditTypeRef = useRef<string | undefined>(undefined);
	const prevInternalReportRef = useRef<boolean | undefined>(undefined);

	useEffect(() => {
		if (reportType !== "audit" || !isOpen || !form.formRef) return;

		const auditType = form.formData?.audit_type;
		const internalReport = form.formData?.internal_report;
		const masterTenantKey = import.meta.env.VITE_MASTER_TENANT_KEY;

		const auditTypeChanged = auditType !== prevAuditTypeRef.current;
		const internalReportChanged =
			internalReport !== prevInternalReportRef.current;

		if (
			internalReportChanged &&
			!internalReport &&
			auditType === "management_review"
		) {
			form.formRef.setFieldValue("audit_type", undefined);
			form.formRef.setFieldValue("auditee", undefined);
		} else if (auditTypeChanged) {
			if (auditType === "management_review") {
				form.formRef.setFieldValue("auditee", masterTenantKey);
			} else if (prevAuditTypeRef.current === "management_review") {
				form.formRef.setFieldValue("auditee", undefined);
			}
		}

		prevAuditTypeRef.current = auditType;
		prevInternalReportRef.current = internalReport;
	}, [
		reportType,
		isOpen,
		form.formRef,
		form.formData?.audit_type,
		form.formData?.internal_report,
	]);

	// MUTATIONS
	const createMutation = useMutation({
		mutationFn: (data: any) =>
			crud.create(
				sanitizeReportFormData(
					overlayServerFileTruth(data, reportData),
					reportType
				),
				currentUser,
				currentTenantKey
			),
		onMutate: () => {
			setIsSaving(true);
		},
		onSuccess: (data) => {
			handleInvalidateQueries(data);
			if (!data) return;
			// Switch to edit mode — setReportData triggers initialData to update,
			// which the separate useEffect([initialData]) picks up automatically.
			setReportData(data);
			setInternalActiveId(data._id);
			setCreatedRecordId(data._id);
		},
		onSettled: () => {
			setIsSaving(false);
		},
	});

	const updateMutation = useMutation({
		mutationFn: ({
			id,
			data,
			options = { upsert: true, new: true },
		}: {
			id: string;
			data: any;
			options?: any;
		}) =>
			crud.update(
				id,
				sanitizeReportFormData(
					overlayServerFileTruth(data, reportData),
					reportType
				),
				currentUser,
				currentTenantKey,
				options
			),
		onMutate: () => {
			setIsSaving(true);
		},
		onSuccess: (data) => {
			handleInvalidateQueries(data);
			if (!data) return;
			setReportData(data);
		},
		onSettled: () => {
			setIsSaving(false);
		},
	});

	const deleteMutation = useMutation({
		mutationFn: ({ id, operator }: { id: string; operator: string }) =>
			crud.delete(id, operator),
		onMutate: () => {
			setIsSaving(true);
		},
		onSuccess: () => {
			handleInvalidateQueries();
			if (reportData?.folder_id) {
				deleteFiles(
					reportData.operator,
					[reportData.folder_id],
					null,
					null
				).catch((error: any) => {
					const message = getSharePointErrorMessage(error);
					alertMessage(
						"error",
						"The report was deleted, but its SharePoint folder could not be removed.",
						message
					);
				});
			}
			handleClose();
		},
		onSettled: () => {
			setIsSaving(false);
		},
	});

	// HELPER FUNCTIONS
	const applyServerUpdate = (updated: any) => {
		setReportData((prev: any) => (prev ? { ...prev, ...updated } : updated));
	};

	/** Persist attachment state (ids, flags, folder) without touching status. */
	const persistAttachmentState = async (
		reportId: string,
		data: Record<string, any>
	) => {
		const res: any = await axios.put(
			`${url}/${getReportApiPath(reportType)}`,
			{ filter: { _id: reportId }, data },
			{ ...AuthHeader }
		);
		return res.data?.[0] ?? {};
	};

	const describeFailures = (
		response: any
	): { name: string; reason: string }[] => [
		...[
			...(response?.failed_uploads ?? []),
			...(response?.failed_removals ?? []),
		].map((file: any) => ({
			name: file.file_name,
			reason: getSharePointErrorMessage(file.error, file.file_name),
		})),
		// Folder-level problems (a nested folder that could not be prepared, an
		// action whose documents were skipped) arrive as warnings, not per file.
		...(response?.warnings ?? [])
			.filter((warning: unknown) => typeof warning === "string" && warning)
			.map((warning: string) => ({ name: "SharePoint", reason: warning })),
	];

	/**
	 * Sync the persisted report's attachments with SharePoint. `baseline` is the
	 * last server copy whose files were synced: entries present there but gone
	 * from `saved` are deleted from SharePoint. Works from the persisted copy
	 * rather than form state so it cannot race the form reset after a save.
	 */
	const syncReportFiles = async (
		saved: any,
		baseline: any
	): Promise<{ name: string; reason: string }[]> => {
		const failedFiles: { name: string; reason: string }[] = [];
		const ownerEmail = currentUser?.email;
		const tenantKey = saved.operator;
		const sharepointType = getReportSharepointType(reportType);
		let current = saved;
		let folderRecreated = false;

		const rememberSynced = (updated: any, sent: Record<string, any>) => {
			// An empty update response would otherwise leave the notice and the
			// removal baseline behind what was actually persisted.
			const applied =
				updated && Object.keys(updated).length > 0 ? updated : sent;
			current = { ...current, ...applied };
			lastSyncedReportDataRef.current = current;
			applyServerUpdate(applied);
		};

		// ─── Report-level uploads ("Additional documents") ───────────────────
		const field = getReportFileField(reportType);
		const savedFiles: ReportFileEntry[] | undefined = Array.isArray(
			saved[field]
		)
			? saved[field]
			: undefined;
		const removal = pickRemovalBaseline<ReportFileEntry>(
			savedFiles,
			baseline?.[field]
		);
		if (removal.suppressed) {
			console.warn(
				`[saveReport] ${field} was persisted empty while the last synced copy had files; SharePoint removals skipped.`
			);
		}

		const response: any = await postSaveReportFiles(tenantKey, {
			type: sharepointType,
			folder_id: saved.folder_id,
			report_number: saved.report_number,
			_id: saved._id,
			folder_name: getReportUploadFolderName(reportType),
			upload_path: "uploads",
			file_list: savedFiles ?? [],
			initial_file_list: removal.baseline,
		});
		failedFiles.push(...describeFailures(response));
		folderRecreated = !!response?.folder_recreated;

		const folderChanged =
			!!response?.folder_id && response.folder_id !== saved.folder_id;
		const filesChanged =
			!!response?.added_files?.length || !!response?.failed_uploads?.length;
		if (folderChanged || filesChanged) {
			const update: Record<string, any> = {
				folder_id: response.folder_id ?? saved.folder_id,
			};
			if (savedFiles) {
				update[field] = applyPostSaveResult(savedFiles, response, ownerEmail);
				// Put the ids into the form before the PUT: if the PUT fails, the
				// next save still knows these files are already in SharePoint.
				form.formRef?.setFieldValue?.(field, update[field]);
			}
			const updated = await persistAttachmentState(saved._id, update);
			rememberSynced(updated, update);
		}

		// ─── Action / finding documents in nested folders ─────────────────────
		const { listName, parentFolderName, idField } =
			getNestedListConfig(reportType);
		const currentItems: any[] | undefined = Array.isArray(saved[listName])
			? saved[listName]
			: undefined;

		if (currentItems) {
			const itemRemoval = pickRemovalBaseline<any>(
				currentItems,
				baseline?.[listName]
			);
			if (itemRemoval.suppressed) {
				console.warn(
					`[saveReport] ${listName} was persisted empty while the last synced copy had items; SharePoint folder removals skipped.`
				);
			}
			const initialItems: any[] = itemRemoval.baseline;
			const currentIds = new Set(
				currentItems.map((item: any) => item?.[idField])
			);
			const removedReferenceIds = initialItems
				.map((item: any) => item?.[idField])
				.filter((id: any) => id && !currentIds.has(id));

			// Items without documents on either side need no folder work.
			const itemsPayload = currentItems.flatMap((item: any) => {
				const referenceId = item?.[idField];
				if (!referenceId) return [];
				const initialItem = initialItems.find(
					(i: any) => i?.[idField] === referenceId
				);
				const fileList = item?.documents ?? [];
				const initialFileList = initialItem?.documents ?? [];
				if (!fileList.length && !initialFileList.length) return [];
				return [
					{
						reference_id: referenceId,
						file_list: fileList,
						initial_file_list: initialFileList,
					},
				];
			});

			if (itemsPayload.length || removedReferenceIds.length) {
				const nested: any = await syncNestedDocuments(tenantKey, {
					type: sharepointType,
					report_number: saved.report_number,
					folder_id: response?.folder_id ?? current.folder_id ?? saved.folder_id,
					_id: saved._id,
					upload_path: "uploads",
					parent_folder_name: parentFolderName,
					items: itemsPayload,
					removed_reference_ids: removedReferenceIds,
				});
				failedFiles.push(...describeFailures(nested));
				folderRecreated = folderRecreated || !!nested?.folder_recreated;

				const resultItems: any[] = nested?.items ?? [];
				const nestedChanged =
					(!!nested?.folder_id && nested.folder_id !== current.folder_id) ||
					resultItems.some((result: any) => result.files?.length) ||
					!!nested?.failed_uploads?.length;

				if (nestedChanged) {
					const resultsByReference = new Map(
						resultItems.map((result: any) => [result.reference_id, result])
					);
					const updatedItems = currentItems.map((item: any) => {
						const result = resultsByReference.get(item?.[idField]);
						if (!result || !Array.isArray(item?.documents)) return item;
						return {
							...item,
							documents: applyPostSaveResult(
								item.documents,
								{
									added_files: result.files,
									failed_uploads: nested?.failed_uploads,
								},
								ownerEmail
							),
						};
					});
					const nestedUpdate = {
						folder_id: nested?.folder_id ?? current.folder_id,
						[listName]: updatedItems,
					};
					form.formRef?.setFieldValue?.(listName, updatedItems);
					const updated = await persistAttachmentState(saved._id, nestedUpdate);
					rememberSynced(updated, nestedUpdate);
				}
			}
		}

		if (folderRecreated) {
			alertMessage(
				"info",
				"The report's SharePoint folder was missing and has been recreated."
			);
		}

		return failedFiles;
	};

	/**
	 * Persist the form, then sync attachments with SharePoint. Persist errors
	 * propagate unchanged; a sync failure is reported in the result because the
	 * report itself is already saved.
	 */
	const saveReport = async (
		data: any,
		{ mode }: SaveReportOptions
	): Promise<SaveReportResult> => {
		const persist = () => {
			if (mode === "create") return createMutation.mutateAsync(data);
			if (!internalActiveId) {
				throw new Error(
					"Cannot update a report that has not been created yet."
				);
			}
			return updateMutation.mutateAsync({ id: internalActiveId, data });
		};

		const baseline = lastSyncedReportDataRef.current;
		const saved = await persist();

		if (!saved?._id) {
			throw new Error(
				"The report was saved but the server returned no record."
			);
		}

		// From here on the persisted copy is the removal baseline, so a failed
		// sync can never make the next save treat these files as removed.
		lastSyncedReportDataRef.current = saved;

		try {
			const failedFiles = await syncReportFiles(saved, baseline);
			return {
				saved,
				sync: {
					hasFailures: failedFiles.length > 0,
					failedFiles: failedFiles.length > 0 ? failedFiles : undefined,
				},
			};
		} catch (error) {
			console.error("[saveReport] Attachment sync failed:", error);
			return {
				saved,
				sync: {
					hasFailures: true,
					failedFiles: [
						{ name: "Attachments", reason: getSharePointErrorMessage(error) },
					],
				},
			};
		}
	};

	// Put a saved report into its stream list straight from the save response,
	// so the row shows without waiting for the list refetch. Streams only list
	// submitted reports, so a report saved as draft is taken out instead.
	const updateStreamRow = (saved: any, { insertOnly = false } = {}) => {
		if (!saved?._id) return;
		queryClient.setQueryData(
			["reporting-hub-data", reportType],
			(rows: any[] | undefined) => {
				if (!Array.isArray(rows)) return rows;
				const isSaved = (row: any) => row?._id === saved._id;
				const exists = rows.some(isSaved);
				if (saved.status === "draft") {
					return exists ? rows.filter((row) => !isSaved(row)) : rows;
				}
				if (!exists) return [{ key: saved._id, ...saved }, ...rows];
				if (insertOnly) return rows;
				return rows.map((row) => (isSaved(row) ? { ...row, ...saved } : row));
			}
		);
	};

	const handleInvalidateQueries = (saved?: any) => {
		void queryClient.invalidateQueries({ queryKey: ["my_tasks"] });
		updateStreamRow(saved);
		void queryClient
			.invalidateQueries({ queryKey: ["reporting-hub-data", reportType] })
			// The list read can come back without the report that was just saved;
			// keep its row until a later fetch includes it.
			.then(() => updateStreamRow(saved, { insertOnly: true }));
		void queryClient.invalidateQueries({ queryKey: ["reference_data"] });
		void queryClient.invalidateQueries({ queryKey: ["my_reports"] });
		void queryClient.invalidateQueries({ queryKey: ["control_center_counts"] });
	};

	const handleClose = () => {
		// Reset form fields
		form.resetForm();
		// Reset local states
		setInternalActiveId(undefined);
		setCreatedRecordId(null);
		setReportData(null);
		lastSyncedReportDataRef.current = null;
		pendingReportDataRef.current = null;
		setIsSaving(false);
		setIsLoadingData(false);
		setIsEnteringEditMode(false);
		// Close drawer
		onClose();
	};

	const handleDelete = () => {
		if (!reportData) return;
		deleteMutation.mutate({
			id: reportData._id,
			operator: reportData.operator,
		});
	};

	const handleEnterEditMode = async () => {
		setIsEnteringEditMode(true);
		try {
			const result = await acquire();
			if (!result) return;
			const { report_id, report_type, ...lockFields } = result;
			setReportData((prev: any) => (prev ? { ...prev, ...lockFields } : prev));
		} catch (error: any) {
			if (axios.isAxiosError(error) && error.response?.status === 409) {
				alertMessage(
					"error",
					"Unable to enter edit mode. The report is currently being edited by another user."
				);
			} else {
				alertMessage(
					"error",
					getErrorMessage(error, "Unable to enter edit mode. Please try again.")
				);
			}
		} finally {
			setIsEnteringEditMode(false);
		}
	};

	const handleExitEditMode = async () => {
		try {
			await release();
			setReportData((prev: any) =>
				prev
					? {
							...prev,
							locked_by_email: null,
							locked_by_name: null,
							locked_at: null,
						}
					: prev
			);
		} catch (error: any) {
			alertMessage(
				"error",
				getErrorMessage(error, "Unable to exit edit mode. Please try again.")
			);
		}
	};

	const handleForceUnlock = async () => {
		try {
			await forceUnlock();
			setReportData((prev: any) =>
				prev
					? {
							...prev,
							locked_by_email: null,
							locked_by_name: null,
							locked_at: null,
						}
					: prev
			);
			alertMessage("success", "Report unlocked.");
		} catch (error: any) {
			alertMessage(
				"error",
				getErrorMessage(error, "Failed to force unlock the report.")
			);
		}
	};

	// RENDER FUNCTIONS
	const renderTitle = () => {
		// Control Centre rows carry report_type; an empty one falls back too.
		const statusReportType = reportData?.report_type || reportType; // NOSONAR: an empty report_type falls back to the drawer's
		const title = TITLE_MAP[reportType] ?? "Unknown Type";

		const getStatusDisplayInfo = () => {
			if (isLoadingData) return { text: "Loading...", color: "#D9D9D9" };
			if (!reportData) return { text: "Draft", color: "#D9D9D9" };

			// Use combined_status if available (from ControlCenter API)
			if (reportData.combined_status) {
				return {
					text:
						STATUS_TEXT_MAP[reportData.combined_status] ||
						reportData.combined_status,
					color: STATUS_COLOR_MAP[reportData.combined_status] || "#FFFFFF",
				};
			}

			if (reportData.status === "draft") {
				return { text: "Draft", color: "#D9D9D9" };
			}

			if (reportData.status === "closed") {
				return { text: "Approved and closed", color: "#00FF75" };
			}

			// Open status
			const openStatus = reportData.open_status;
			const text =
				OPEN_STATUS_MAP[openStatus as keyof typeof OPEN_STATUS_MAP] ||
				openStatus;
			return { text: text || "Unknown", color: "#13C2C2" };
		};

		const statusInfo = getStatusDisplayInfo();

		return (
			<div className="flex flex-nowrap items-center justify-between w-full">
				<div className="flex items-center gap-2">
					<p>{title}</p>
					<Badge
						color={statusInfo.color}
						text={<span style={{ color: "#fff" }}>{statusInfo.text}</span>}
					/>
				</div>
				<div className="flex items-center gap-2">
					{/* To be fixed: Bug in the update status form */}
					{showUpdateStatus &&
						canUpdateStatus &&
						internalActiveId &&
						reportData &&
						!isFormReadOnly && (
							<ReportStatusUpdateModal
								reportId={reportData._id}
								reportType={statusReportType}
								operator={reportData.operator}
								taskId={taskId}
								currentStatus={(() => {
									// Compute combined status same as backend get_combined_status()
									const status = reportData.status;
									const openStatus = reportData.open_status;
									const isRejected = reportData.is_rejected;

									// Draft status
									if (status === "draft") return "draft";

									// Open status - use open_status sub-state
									if (status === "open") {
										if (openStatus === "in_review") return "in_review";
										return openStatus;
									}

									// Closed status - check if rejected or approved
									if (status === "closed") {
										if (isRejected) return "closed_rejected";
										return "closed_approved";
									}

									return status;
								})()}
								currentAssignedTo={(() => {
									// Compute combined status same as above
									const status = reportData.status;
									const openStatus = reportData.open_status;
									const isRejected = reportData.is_rejected;
									let combinedStatus;

									if (status === "draft") combinedStatus = "draft";
									else if (status === "open") {
										if (openStatus === "in_review")
											combinedStatus = "in_review";
										else combinedStatus = openStatus;
									} else if (status === "closed") {
										if (isRejected) combinedStatus = "closed_rejected";
										else combinedStatus = "closed_approved";
									} else combinedStatus = status;

									// Determine assigned person based on report status
									switch (combinedStatus) {
										case "draft":
										case "changes_required":
											return getReportAuthorEmail(reportData);
										case "in_review":
										case "final_review":
										case "ready_to_close":
											return reportData.reviewer;
										case "in_progress":
										case "revision_required": {
											// Get responsible person from follow_up_actions or findings
											const actions =
												reportData.follow_up_actions ??
												reportData.findings ??
												[];
											if (actions.length > 0 && actions[0].responsible) {
												return actions[0].responsible;
											}
											return getReportAuthorEmail(reportData);
										}
										case "closed_approved":
										case "closed_rejected":
											return undefined;
										default:
											return getReportAuthorEmail(reportData);
									}
								})()}
								onSuccess={() => {
									// Refetch report data to show updated status
									if (internalActiveId) {
										const fetchFn = REPORT_DATA_API[reportType];
										if (fetchFn) {
											void fetchFn({ _id: internalActiveId }).then(
												(result: any) => {
													if (Array.isArray(result))
														setReportData(result[0] ?? null);
												}
											);
										}
									}
								}}
							/>
						)}
					{internalActiveId && reportData && (
						<AuditLogButton
							moduleKey="reporting"
							handleClick={() => setAuditLogOpen(true)}
						/>
					)}
				</div>
			</div>
		);
	};

	const renderFooter = () => {
		if (isLockedByOther) {
			return (
				<div className="flex justify-between items-center w-full">
					<span className="text-sm">
						This report is currently being edited by {lockOwner}. Close the
						drawer to continue.
					</span>
					<Button onClick={handleClose}>Close</Button>
				</div>
			);
		}

		if (isExistingDraft && !isEditing) {
			return (
				<div className="flex justify-between items-center w-full">
					<span className="text-sm text-gray-500">
						{isEnteringEditMode ? "Entering edit mode..." : "View mode"}
					</span>
					<Button onClick={handleClose} loading={isEnteringEditMode}>
						Close
					</Button>
				</div>
			);
		}

		const FooterComponent = FOOTER_COMPONENT_MAP[reportType];
		if (!FooterComponent) return null;
		return (
			<div className="flex justify-between items-center w-full">
				<FooterComponent
					form={form}
					reportContext={reportContext}
					isSaving={isSaving}
					isLoading={isLoadingData}
					handleClose={handleClose}
					handleDelete={handleDelete}
					updateMutation={updateMutation}
					saveReport={saveReport}
					onValidationError={handleValidationError}
					onValidationSuccess={handleValidationSuccess}
					onExitEditMode={handleExitEditMode}
				/>
			</div>
		);
	};

	const renderLockBanner = () => {
		if (!isLockedByOther) return null;
		return (
			<div
				className="w-full border-b border-l-20 py-[16px] px-[18px] flex items-center gap-2"
				style={{
					borderColor: "var(--default-btn-color)",
					backgroundColor: "#fff2f0",
					color: "#cf1322",
				}}
			>
				<svg
					aria-hidden="true"
					xmlns="http://www.w3.org/2000/svg"
					width="16"
					height="16"
					viewBox="0 0 24 24"
					fill="currentColor"
				>
					<path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z" />
				</svg>
				<span className="font-medium">
					This report is currently being edited by <strong>{lockOwner}</strong>
					{
						". You can continue viewing the report, but editing is temporarily disabled."
					}
				</span>
			</div>
		);
	};

	const renderComment = () => {
		if (
			!reportData ||
			!["changes_required", "revision_required"].includes(
				reportData.open_status
			)
		)
			return null;
		return (
			<div
				style={{
					borderColor: "var(--default-btn-color)",
					backgroundColor: "var(--container-bg-color)",
				}}
				className="w-full border-b border-l-20 py-[30px] px-[18px] flex flex-col gap-y-2"
			>
				<p className="font-bold">Comments from the reviewer:</p>
				<p>{reportData.request_changes_comment}</p>
			</div>
		);
	};

	const AUDIT_LOG_LABEL_MAP = {
		audit: "Audit",
		incident_report: "Incident report",
		moc: "Management of Change",
		offi: "Opportunity for Improvements report",
		non_conformance: "Non-conformance report",
		lesson_learned: "Lesson learned",
	} as const;

	return (
		<>
			<CustomDrawer
				title={renderTitle()}
				open={isOpen}
				close={handleClose}
				footer={renderFooter()}
				maskClosable={!form.isDirty}
				classNames={{
					body: "p-0!",
				}}
			>
				<div className="flex flex-col relative w-full h-full">
					{renderLockBanner()}
					{renderComment()}
					<FailedAttachmentsNotice attachments={failedAttachments} />
					{hasTabs && (
						<div className="px-8 pt-3 flex justify-between items-center gap-4">
							<ConfigProvider
								theme={{ components: { Tabs: { horizontalMargin: "0" } } }}
							>
								<Tabs
									activeKey={activeTab}
									onChange={(key) => {
										setActiveTab(key);
									}}
									items={visibleTabs.map((tab) => {
										const count =
											validationErrorCounts[tab.key] ?? tabCounts[tab.key] ?? 0;
										return {
											key: tab.key,
											label: (
												<span className="flex items-center gap-2">
													{tab.label}
													{showTabErrorCounts && count > 0 && (
														<Badge
															size="small"
															count={count}
															showZero={false}
															styles={{
																indicator: {
																	fontSize: 10,
																	lineHeight: "16px",
																},
															}}
														/>
													)}
												</span>
											),
										};
									})}
								/>
							</ConfigProvider>
							{showEditButton && (
								<Button
									type={isEditing ? "default" : "primary"}
									loading={isLockLoading && (isEditing || isEnteringEditMode)}
									disabled={isLockLoading && !isEditing && !isEnteringEditMode}
									onClick={isEditing ? handleExitEditMode : handleEnterEditMode}
								>
									{editButtonLabel}
								</Button>
							)}
							{isLockedByOther && isSuperAdmin && (
								<Popconfirm
									title="Force unlock this report?"
									description="The current editor will be returned to view mode and any unsaved changes will be lost."
									onConfirm={handleForceUnlock}
									okText="Unlock"
									cancelText="Cancel"
								>
									<Button loading={isLockLoading}>Force unlock</Button>
								</Popconfirm>
							)}
						</div>
					)}
					<div className="report-form-scroll flex-1 overflow-y-auto px-8 py-3">
						<div
							className="group"
							data-active-tab={getTabDataAttributeValue(activeTab)}
						>
							<FormController
								formController={form}
								theme={"dark"}
								httpAgent={httpAgent}
								enablePostSave={true}
								resetOnDependencyChange={true}
								resetOnDependencyEmpty={true}
								readOnly={isFormReadOnly}
							/>
						</div>
					</div>
					{(isSaving || isLoadingData) && (
						<div className="flex h-full w-full justify-center items-center absolute bg-black/30">
							<Spin />
						</div>
					)}
				</div>
			</CustomDrawer>
			<AuditLogModal
				auditlogFilter={{
					level: reportType,
					level_id: reportData?._id,
				}}
				label={
					AUDIT_LOG_LABEL_MAP[reportType as keyof typeof AUDIT_LOG_LABEL_MAP] ??
					"Report"
				}
				type={reportType === "lesson_learned" ? "lesson_learned" : "reporting"}
				open={auditLogOpen}
				handleClose={() => setAuditLogOpen(false)}
			/>
		</>
	);
};

export default ReportingFormDrawerV2;
